/**
 * GuidedTourStep — step 4 of the onboarding wizard.
 * Renders a spotlight overlay + tooltip over each tour target in sequence.
 * Skips missing DOM targets and logs a warning (AC7).
 * Escape key closes tour and advances to step 5 with telemetry (AC8).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { TourTarget } from './types';
import { onboardingApi } from '../../api/onboarding';

export const TOUR_TARGETS: TourTarget[] = [
  {
    selector: '[data-tour="sidebar"]',
    title: 'Sidebar Navigation',
    description: 'Access all features from the sidebar. Each section is one click away.',
    position: 'right',
  },
  {
    selector: '[data-tour="search"]',
    title: 'Global Search',
    description: 'Search the knowledge base, tickets, and content instantly.',
    position: 'bottom',
  },
  {
    selector: '[data-tour="ai-assistant"]',
    title: 'AI Assistant',
    description: 'Get instant answers and automate tasks with your AI assistant.',
    position: 'left',
  },
  {
    selector: '[data-tour="dashboard-widgets"]',
    title: 'Dashboard Widgets',
    description: 'Monitor your key metrics and recent activity at a glance.',
    position: 'top',
  },
];

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface SpotlightOverlayProps {
  rect: SpotlightRect;
}

/** Four-panel dark backdrop with a spotlight cutout. */
function SpotlightOverlay({ rect }: SpotlightOverlayProps) {
  const pad = 8;
  const x = rect.left - pad;
  const y = rect.top - pad;
  const w = rect.width + pad * 2;
  const h = rect.height + pad * 2;
  const bg = 'rgba(0,0,0,0.7)';

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }}
      aria-hidden="true"
    >
      {/* Top */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: y, background: bg }} />
      {/* Bottom */}
      <div style={{ position: 'absolute', top: y + h, left: 0, right: 0, bottom: 0, background: bg }} />
      {/* Left */}
      <div style={{ position: 'absolute', top: y, left: 0, width: x, height: h, background: bg }} />
      {/* Right */}
      <div style={{ position: 'absolute', top: y, left: x + w, right: 0, height: h, background: bg }} />
      {/* Glow ring around spotlight */}
      <div
        style={{
          position: 'absolute',
          top: y,
          left: x,
          width: w,
          height: h,
          borderRadius: 8,
          boxShadow: '0 0 0 2px rgba(255,255,255,0.8), 0 0 20px rgba(255,255,255,0.3)',
        }}
      />
    </div>
  );
}

interface TourTooltipProps {
  target: TourTarget;
  targetRect: SpotlightRect;
  currentIndex: number;
  total: number;
  onNext: () => void;
  onSkip: () => void;
}

const TOOLTIP_OFFSET = 16;

function tooltipStyle(position: TourTarget['position'], rect: SpotlightRect): React.CSSProperties {
  const pad = 8;
  const x = rect.left - pad;
  const y = rect.top - pad;
  const w = rect.width + pad * 2;
  const h = rect.height + pad * 2;

  switch (position) {
    case 'right':
      return { position: 'fixed', top: y + h / 2, left: x + w + TOOLTIP_OFFSET, transform: 'translateY(-50%)', zIndex: 10000 };
    case 'left':
      return { position: 'fixed', top: y + h / 2, right: `calc(100vw - ${x - TOOLTIP_OFFSET}px)`, transform: 'translateY(-50%)', zIndex: 10000 };
    case 'bottom':
      return { position: 'fixed', top: y + h + TOOLTIP_OFFSET, left: Math.max(8, x + w / 2 - 150), zIndex: 10000 };
    case 'top':
    default:
      return { position: 'fixed', bottom: `calc(100vh - ${y - TOOLTIP_OFFSET}px)`, left: Math.max(8, x + w / 2 - 150), zIndex: 10000 };
  }
}

function TourTooltip({ target, targetRect, currentIndex, total, onNext, onSkip }: TourTooltipProps) {
  const nextRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    nextRef.current?.focus();
  }, [currentIndex]);

  return (
    <div
      style={tooltipStyle(target.position, targetRect)}
      className="w-72 animate-in fade-in rounded-lg bg-white p-4 shadow-xl ring-1 ring-gray-900/5 duration-200"
      role="dialog"
      aria-label={`Tour step ${currentIndex + 1} of ${total}: ${target.title}`}
    >
      <h3 className="mb-1 text-sm font-semibold text-gray-900">{target.title}</h3>
      <p className="mb-4 text-xs leading-relaxed text-gray-600">{target.description}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400">
          {currentIndex + 1} / {total}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="rounded px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400"
          >
            Skip Tour
          </button>
          <button
            ref={nextRef}
            type="button"
            onClick={onNext}
            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {currentIndex === total - 1 ? 'Done ✓' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface GuidedTourStepProps {
  /** Current tour target index (0-3). */
  tourProgress: number;
  onTourComplete: () => void;
  onTourSkip: () => void;
  onProgressChange: (progress: number) => void;
}

export function GuidedTourStep({
  tourProgress,
  onTourComplete,
  onTourSkip,
  onProgressChange,
}: GuidedTourStepProps) {
  const [targetRect, setTargetRect] = useState<SpotlightRect | null>(null);
  const [activeIndex, setActiveIndex] = useState(tourProgress);

  /** Find the next valid (present in DOM) target index starting from `from`. */
  function findNextValidIndex(from: number): number | null {
    for (let i = from; i < TOUR_TARGETS.length; i++) {
      if (document.querySelector(TOUR_TARGETS[i].selector) !== null) return i;
      console.warn(`Tour target not found: ${TOUR_TARGETS[i].selector}`);
    }
    return null;
  }

  const measureTarget = useCallback((index: number) => {
    const valid = findNextValidIndex(index);
    if (valid === null) {
      // All remaining targets missing — skip tour entirely
      onTourSkip();
      return;
    }
    if (valid !== index) {
      setActiveIndex(valid);
      onProgressChange(valid);
      return;
    }
    const el = document.querySelector(TOUR_TARGETS[index].selector);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    setActiveIndex(index);
  }, [onProgressChange, onTourSkip]);

  // Measure whenever activeIndex changes
  useEffect(() => {
    measureTarget(activeIndex);
  }, [activeIndex, measureTarget]);

  // Escape key → skip tour with telemetry (AC8)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onboardingApi.trackEvent('onboarding_tour_skipped', { step: activeIndex });
        onTourSkip();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, onTourSkip]);

  function handleNext() {
    const nextIndex = activeIndex + 1;
    if (nextIndex >= TOUR_TARGETS.length) {
      onProgressChange(TOUR_TARGETS.length); // mark all complete
      onTourComplete();
      return;
    }
    measureTarget(nextIndex);
    onProgressChange(nextIndex);
  }

  function handleSkip() {
    onboardingApi.trackEvent('onboarding_tour_skipped', { step: activeIndex });
    onTourSkip();
  }

  if (!targetRect) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        <p className="text-sm text-gray-500">Preparing tour…</p>
      </div>
    );
  }

  const currentTarget = TOUR_TARGETS[activeIndex];

  return (
    <>
      <SpotlightOverlay rect={targetRect} />
      <TourTooltip
        target={currentTarget}
        targetRect={targetRect}
        currentIndex={activeIndex}
        total={TOUR_TARGETS.length}
        onNext={handleNext}
        onSkip={handleSkip}
      />
      {/* Non-overlay content — brief instruction shown behind the overlay */}
      <div className="px-6 py-8 text-center">
        <h2 className="text-xl font-semibold text-gray-900">Interactive Tour</h2>
        <p className="mt-1 text-sm text-gray-500">
          We're highlighting key features. Follow the tooltips to explore Martha.
        </p>
      </div>
    </>
  );
}
